import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo, useRef } from "react";
import type { StylesConfig } from "react-select";
import { toast } from "react-toastify";
import { ImportableLockbox, BulkImportStatus } from "api/lockboxImport";
import { lockboxAccountsApi } from "api/lockbox-accounts";
import type { LockboxIntegratedVendor } from "api/landlord/lockboxes/types";
import { SquareLock02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { getErrorMessage } from "utils/error";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CheckboxSize,
  InputSize,
  Modal,
  Select,
  Show
} from "shared/components";
import { Loader } from "shared/components/Loader";

const selectStyles: StylesConfig = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 })
};

interface ImportLockboxesModalApi {
  getLockboxes: (accountId: number) => Promise<ImportableLockbox[]>;
  bulkImportLockboxes: (
    accountId: number,
    serialNumbers: string[]
  ) => Promise<void>;
  getBulkImportStatus: () => Promise<BulkImportStatus>;
}

interface ImportLockboxesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  vendor: LockboxIntegratedVendor;
  emptyStateDescription: string;
  noAccountsDescription: string;
  api: ImportLockboxesModalApi;
  initialAccountId?: number;
}

const isTerminal = (s: BulkImportStatus) =>
  s.status === "done" || s.status === "failed" || s.status === "not_started";

const MAX_IMPORT_SELECTION = 500;

export const ImportLockboxesModal = ({
  open,
  onClose,
  onSuccess,
  vendor,
  emptyStateDescription,
  noAccountsDescription,
  api,
  initialAccountId
}: ImportLockboxesModalProps) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [accountId, setAccountId] = useState<number | null>(null);
  const seededRef = useRef(false);

  const [isTracking, setIsTracking] = useState(false);

  const accountsQueryKey = useMemo(
    () => ["LOCKBOX_ACCOUNTS", vendor],
    [vendor]
  );
  const lockboxesQueryKey = useMemo(
    () => [vendor, "LOCKBOXES", accountId],
    [vendor, accountId]
  );
  const importStatusQueryKey = useMemo(
    () => [vendor, "BULK_IMPORT_STATUS"],
    [vendor]
  );

  const { data: accounts = [], isLoading: isLoadingAccounts } = useQuery({
    queryKey: accountsQueryKey,
    queryFn: () => lockboxAccountsApi.list(vendor),
    enabled: open
  });

  useEffect(() => {
    if (!open || accountId !== null) return;

    if (accounts.some((account) => account.id === initialAccountId))
      setAccountId(initialAccountId as number);
    else if (accounts.length === 1) setAccountId(accounts[0].id);
  }, [open, accountId, accounts, initialAccountId]);

  const accountOptions = useMemo(
    () =>
      accounts.map((account) => ({ value: account.id, label: account.name })),
    [accounts]
  );

  const { data: lockboxes = [], isLoading } = useQuery({
    queryKey: lockboxesQueryKey,
    queryFn: () => api.getLockboxes(accountId as number),
    enabled: open && accountId !== null,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  const importable = useMemo(
    () => lockboxes.filter((lb) => !lb.exists_in_boom),
    [lockboxes]
  );
  const existing = useMemo(
    () => lockboxes.filter((lb) => lb.exists_in_boom),
    [lockboxes]
  );

  const { data: importStatus } = useQuery({
    queryKey: importStatusQueryKey,
    queryFn: () => api.getBulkImportStatus(),
    enabled: open,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (query) =>
      query.state.data?.status === "pending" ? 1500 : false
  });

  const isPolling = importStatus?.status === "pending";

  // A job was already in flight when the modal opened (e.g. reopened mid-import) — start
  // tracking it the same way a just-triggered import would be tracked.
  useEffect(() => {
    if (isPolling) setIsTracking(true);
  }, [isPolling]);

  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      setIsTracking(false);
      setAccountId(null);
      setSelected(new Set());
    }
  }, [open]);

  useEffect(() => {
    if (!open || isPolling) return;
    if (!seededRef.current && importable.length > 0) {
      seededRef.current = true;
      setSelected(new Set(importable.map((lb) => lb.serial_number)));
    }
  }, [open, isPolling, importable]);

  useEffect(() => {
    if (!importStatus || !isTerminal(importStatus)) return;
    if (!isTracking) return;
    setIsTracking(false);

    if (importStatus.status === "not_started") return;

    queryClient.invalidateQueries({ queryKey: [vendor, "LOCKBOXES"] });

    if (importStatus.status === "failed") {
      toast.error("Import failed");
      return;
    }

    const parts: string[] = [];
    if (importStatus.imported_count > 0)
      parts.push(
        `${importStatus.imported_count} lockbox${importStatus.imported_count !== 1 ? "es" : ""} imported`
      );
    if (importStatus.skipped_count > 0)
      parts.push(`${importStatus.skipped_count} already existed`);
    if (importStatus.failed_count > 0)
      parts.push(`${importStatus.failed_count} failed`);

    if (importStatus.failed_count > 0) toast.warn(parts.join(", "));
    else toast.success(parts.join(", ") || "Import complete");

    if (
      importStatus.failed_count === 0 &&
      (importStatus.imported_count > 0 || importStatus.skipped_count > 0)
    )
      onSuccess();
  }, [importStatus, isTracking, queryClient, onSuccess, vendor]);

  const allImportableSelected =
    importable.length > 0 &&
    importable.every((lb) => selected.has(lb.serial_number));
  const someImportableSelected =
    importable.some((lb) => selected.has(lb.serial_number)) &&
    !allImportableSelected;

  const handleSelectAll = () => {
    if (allImportableSelected) setSelected(new Set());
    else setSelected(new Set(importable.map((lb) => lb.serial_number)));
  };

  const handleToggle = (lockbox: ImportableLockbox) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lockbox.serial_number)) next.delete(lockbox.serial_number);
      else next.add(lockbox.serial_number);
      return next;
    });
  };

  const { mutate: importLockboxes, isPending: isSubmitting } = useMutation({
    mutationKey: [vendor, "LOCKBOXES", "BULK_IMPORT"],
    mutationFn: () =>
      api.bulkImportLockboxes(accountId as number, Array.from(selected)),
    onSuccess: () => {
      queryClient.setQueryData(importStatusQueryKey, { status: "pending" });
      setIsTracking(true);
      queryClient.invalidateQueries({ queryKey: importStatusQueryKey });
    },
    onError: (err) => toast.error(getErrorMessage(err))
  });

  // isTracking closes the gap between the mutation resolving and the status refetch actually
  // reporting "pending" — without it the button briefly re-enables in between.
  const isPending = isSubmitting || isPolling || isTracking;
  const selectionExceedsLimit = selected.size > MAX_IMPORT_SELECTION;

  return (
    <Modal
      opened={open}
      onClose={onClose}
      panelClassName="w-full max-w-[480px] overflow-hidden p-6 space-y-6"
    >
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-content-1000">
          Import lockboxes
        </h2>

        <Show if={!isLoadingAccounts && accounts.length === 0}>
          <p className="text-sm text-content-400">{noAccountsDescription}</p>
        </Show>

        <Show if={accounts.length > 1}>
          <Select
            id="lockbox-import-account"
            size={InputSize.SM}
            label="Account"
            required
            searchable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectStyles}
            value={accountId ?? undefined}
            options={accountOptions}
            placeholder="Select an account"
            onChange={(e) => {
              const raw = e.target.value;
              setAccountId(raw ? Number(raw) : null);
              setSelected(new Set());
              seededRef.current = false;
            }}
          />
        </Show>

        <Show if={accounts.length > 1 && accountId === null}>
          <p className="text-sm text-content-400">
            Select an account to view its lockboxes.
          </p>
        </Show>

        <div className="max-h-[340px] overflow-y-auto">
          <Show if={accountId !== null && isLoading}>
            <div className="flex items-center justify-center py-8">
              <Loader />
            </div>
          </Show>

          <Show if={accountId !== null && !isLoading && lockboxes.length === 0}>
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <div className="flex items-center justify-center size-16 rounded-full bg-alpha-40">
                <HugeiconsIcon
                  icon={SquareLock02Icon}
                  size={28}
                  className="text-content-400"
                />
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <p className="text-sm font-bold text-content-700">
                  No lockboxes found
                </p>
                <p className="text-sm text-content-400">
                  {emptyStateDescription}
                </p>
              </div>
            </div>
          </Show>

          <Show if={accountId !== null && !isLoading && lockboxes.length > 0}>
            <table className="w-full">
              <thead>
                <tr className="h-12">
                  <th className="w-14 align-middle text-left">
                    <Checkbox
                      size={CheckboxSize.SM}
                      checked={allImportableSelected}
                      indeterminate={someImportableSelected}
                      disabled={importable.length === 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-4 align-middle text-left text-xs font-normal text-content-400">
                    Name
                  </th>
                </tr>
              </thead>
              <tbody>
                {importable.map((lb) => (
                  <tr
                    key={lb.serial_number}
                    className="h-12 border-t border-alpha-40 hover:bg-background-900 cursor-pointer"
                    onClick={() => handleToggle(lb)}
                  >
                    <td className="w-14 align-middle">
                      <Checkbox
                        size={CheckboxSize.SM}
                        checked={selected.has(lb.serial_number)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggle(lb);
                        }}
                      />
                    </td>
                    <td className="px-4 align-middle text-sm text-content-1000">
                      {lb.serial_number}
                    </td>
                  </tr>
                ))}
                {existing.map((lb) => (
                  <tr
                    key={lb.serial_number}
                    className="h-12 border-t border-alpha-40"
                  >
                    <td className="w-14 align-middle">
                      <Checkbox
                        size={CheckboxSize.SM}
                        checked={false}
                        disabled
                        onChange={() => undefined}
                      />
                    </td>
                    <td className="px-4 align-middle text-sm text-content-200">
                      {lb.serial_number} has already been added
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Show>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          className="w-[125px]"
          onClick={onClose}
          disabled={isPending}
        >
          Skip
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          className="w-[125px]"
          disabled={
            accountId === null ||
            selected.size === 0 ||
            selectionExceedsLimit ||
            isPending
          }
          loading={isPending}
          onClick={() => importLockboxes()}
          tooltip={
            selectionExceedsLimit
              ? `Select up to ${MAX_IMPORT_SELECTION} at a time`
              : undefined
          }
        >
          Import
        </Button>
      </div>
    </Modal>
  );
};
